/**
 * Demo BETA welcome modal store.
 *
 * Persistence is SESSION-scoped on purpose: the one-time welcome modal fires
 * once per browser *session* (so it never nags on navigation) but greets the
 * visitor again on a fresh session. The demo audience is ephemeral (no server
 * identity), so the "seen" flag lives client-side in ``sessionStorage`` via the
 * shared zustand ``persist`` middleware.
 *
 * Kept SEPARATE from ``demoTourStore`` on purpose: the welcome modal is the
 * very first thing a demo visitor sees (a BETA notice), shown BEFORE and
 * independently of the guided tour. Coupling them would make "seen the modal"
 * and "finished the tour" share a flag and drift.
 *
 * The persistent BETA banner re-opens this same modal via ``reopen()`` — a
 * force-open that bypasses the "seen" flag so "Learn more" always works.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * Zustand-compatible synchronous wrapper around ``window.sessionStorage``.
 *
 * Mirrors ``zustandStorage`` (localStorage) from ``@/lib/storage`` but scopes
 * the dismissal to the current tab session — the once-per-session contract the
 * welcome modal wants. SSR/private-mode safe (every access is guarded).
 */
const sessionZustandStorage = {
  getItem: (name: string): string | null => {
    try {
      return window.sessionStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      window.sessionStorage.setItem(name, value);
    } catch {
      // Quota exceeded / private mode — silently drop.
    }
  },
  removeItem: (name: string): void => {
    try {
      window.sessionStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
};

interface DemoWelcomeState {
  /** Whether the welcome modal is currently open. */
  open: boolean;
  /** Per-audience "already seen this session" flags (keyed like the tour: ``"demo"``). */
  seenBy: Record<string, boolean>;
  /** Open the modal for ``key`` if that audience hasn't seen it this session. */
  openFor: (key: string) => void;
  /** Force-open regardless of the "seen" flag — used by the banner's "Learn more". */
  reopen: () => void;
  /** Close + persist "seen" for ``key`` so it doesn't auto-show again this session. */
  dismiss: (key: string) => void;
}

export const useDemoWelcomeStore = create<DemoWelcomeState>()(
  persist(
    (set, get) => ({
      open: false,
      seenBy: {},
      openFor: (key) => {
        if (get().seenBy[key]) return;
        set({ open: true });
      },
      reopen: () => set({ open: true }),
      dismiss: (key) =>
        set((s) => ({
          open: false,
          seenBy: { ...s.seenBy, [key]: true },
        })),
    }),
    {
      name: "ando.demo-welcome",
      storage: createJSONStorage(() => sessionZustandStorage),
      partialize: (state) => ({ seenBy: state.seenBy }),
    },
  ),
);
